import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import type { VehicleResult, VehicleInput, EmotionalScores } from '../../types';
import { VEHICLE_COLORS } from './TCOSummaryTable';

interface Props {
  vehicles: VehicleResult[];
  vehicleInputs: VehicleInput[];
  financialWeight?: number;
  emotionalWeight?: number;
}

// Radar chart dimensions — exactly the 8 scored fields from EmotionalScores.
// Keys match the actual model field names so the chart shows real user-entered values.
const DIMENSIONS: { label: string; key: keyof EmotionalScores }[] = [
  { label: 'Driving Enjoyment', key: 'driving_enjoyment' },
  { label: 'Powertrain Character', key: 'powertrain_character' },
  { label: 'Practical Utility', key: 'practical_utility' },
  { label: 'Winter Confidence', key: 'winter_confidence' },
  { label: 'Brand Trust', key: 'brand_trust' },
  { label: 'Status / Prestige', key: 'status_prestige' },
  { label: 'Ownership Pride', key: 'ownership_pride' },
  { label: 'Technology / Features', key: 'technology_features' },
];

function buildDimensionScores(emo: EmotionalScores): Record<string, number> {
  return {
    driving_enjoyment: emo.driving_enjoyment,
    powertrain_character: emo.powertrain_character,
    practical_utility: emo.practical_utility,
    winter_confidence: emo.winter_confidence,
    brand_trust: emo.brand_trust,
    status_prestige: emo.status_prestige,
    ownership_pride: emo.ownership_pride,
    technology_features: emo.technology_features,
  };
}

function getNum(v: VehicleResult, section: string, key: string): number | null {
  const sec = v[section as keyof VehicleResult] as Record<string, unknown> | null;
  if (!sec) return null;
  const val = sec[key];
  return typeof val === 'number' ? val : null;
}

function fmt$(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ScoreBadge({ value, max = 10, decimals = 2 }: { value: number; max?: number; decimals?: number }) {
  const pct = (value / max) * 100;
  const color =
    pct >= 70 ? 'text-emerald-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400';
  return <span className={`font-bold tabular-nums ${color}`}>{value.toFixed(decimals)}</span>;
}


export default function EmotionalOverlayPanel({
  vehicles,
  vehicleInputs,
  financialWeight: _financialWeight = 0.6,
  emotionalWeight: _emotionalWeight = 0.4,
}: Props) {
  if (vehicles.length === 0) return null;

  // Build radar chart data: one point per dimension, one value per vehicle.
  // dim.key is a keyof EmotionalOverlayInputs so the value is always a real user-entered score.
  const radarData = DIMENSIONS.map((dim) => {
    const point: Record<string, number | string> = { subject: dim.label };
    vehicleInputs.forEach((vi) => {
      const scores = buildDimensionScores(vi.emotional.scores);
      point[vi.vehicle_id] = scores[dim.key as string];
    });
    return point;
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Per-vehicle metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {vehicles.map((v, idx) => {
          const rawScore = getNum(v, 'emotional', 'raw_weighted_score');
          const normalizedScore = getNum(v, 'emotional', 'normalized_score');
          const costPerUnit = getNum(v, 'emotional', 'cost_per_enjoyment_unit');
          const color = VEHICLE_COLORS[idx] ?? '#94a3b8';

          return (
            <div
              key={v.vehicle_id}
              className="rounded-xl p-4 flex flex-col gap-3" style={{ background: 'var(--panel)', border: '1px solid var(--border)' }}
            >
              <p className="text-sm font-semibold leading-snug" style={{ color }}>
                {v.label}
              </p>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs" style={{ color: 'var(--white-dim)' }}>Raw Emotional Score</span>
                  <span className="text-sm">
                    {rawScore !== null ? (
                      <>
                        <ScoreBadge value={rawScore} max={5} />
                        <span className="text-xs" style={{ color: 'var(--white-dim)' }}> / 5</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--white-dim)' }}>—</span>
                    )}
                  </span>
                </div>

                <div className="flex justify-between items-baseline">
                  <span className="text-xs" style={{ color: 'var(--white-dim)' }}>Normalized Score</span>
                  <span className="text-sm">
                    {normalizedScore !== null ? (
                      <>
                        <ScoreBadge value={normalizedScore * 100} max={100} decimals={1} />
                        <span className="text-xs" style={{ color: 'var(--white-dim)' }}> / 100</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--white-dim)' }}>—</span>
                    )}
                  </span>
                </div>

                <div className="flex flex-col gap-0.5">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs" style={{ color: 'var(--white-dim)' }} title="Total cost divided by emotional score for this vehicle. Lower means more satisfaction per dollar spent. This is an absolute metric, not relative to other vehicles.">
                      Cost / Enjoyment Unit ⓘ
                    </span>
                    <span className="text-sm font-mono text-xs" style={{ color: 'var(--white)' }}>
                      {costPerUnit !== null
                        ? fmt$(costPerUnit) + ' / pt'
                        : '—'}
                    </span>

                  </div>
                </div>


              </div>
            </div>
          );
        })}
      </div>

      {/* Overlaid radar chart — all vehicles on one chart */}
      <div className="rounded-xl p-5" style={{ background: 'var(--panel)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold uppercase mb-4" style={{ color: 'var(--white-dim)', fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.2em' }}>
          Emotional Category Scores (All Vehicles)
        </p>
        <ResponsiveContainer width="100%" height={340}>
          <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <PolarGrid stroke="#1e2530" />
            <PolarAngleAxis
              dataKey="subject"
              tick={{ fill: '#8a93a2', fontSize: 14 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 5]}
              tick={{ fill: '#8a93a2', fontSize: 13 }}
              tickCount={6}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#111418',
                border: '1px solid #1e2530',
                borderRadius: '8px',
                fontSize: '15px',
                color: '#dde3ec',
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '15px', color: '#8a93a2', paddingTop: '12px' }}
            />
            {vehicles.map((v, idx) => (
              <Radar
                key={v.vehicle_id}
                name={v.label}
                dataKey={v.vehicle_id}
                stroke={VEHICLE_COLORS[idx] ?? '#94a3b8'}
                fill={VEHICLE_COLORS[idx] ?? '#94a3b8'}
                fillOpacity={0.12}
                strokeWidth={2}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      </div>


    </div>
  );
}
